<template>
  <div>
    <b-modal
      v-model="showSwitchDateModal"
      :title="$t('settings.analytics.switchDateModalTitle')"
      ok-only
      ok-variant="primary"
      :ok-title="$t('buttons.ok')"
      centered
    >
      <div class="text-center p-4">
        <p v-html="$t('settings.analytics.switchDateModalText', { switchDate: switchDateFormat })"></p>
        <b-button
          variant="link"
          class="mt-3"
          @click="openEmail"
        >
          {{ $t('settings.analytics.contactSupport') }}
        </b-button>
      </div>
    </b-modal>
    <b-row class="row mb-4 mt-5">
      <b-col cols="12" sm="12">
        <div class="analytics-dashboard-header d-flex justify-content-between align-items-end flex-wrap">
          <div class="d-flex align-items-center">
            <h3>{{ $t('settings.analytics.name') }}</h3>
            <b-dropdown
              class="context-menu ml-2"
              toggle-class="dot-button"
              no-caret
              no-flip
              right
            >
              <template #button-content>
                <b-icon icon="three-dots-vertical" aria-hidden="true" scale="1"></b-icon>
              </template>
              <b-dropdown-item @click="downloadCSV">
                {{ $t('settings.analytics.downloadCSV') || 'Download as CSV' }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <analytics-date-range-picker
            v-model="dateRange"
            :min-date="minDate"
            @input="updateValues"
          />
        </div>
        <hr>
        <p v-html="$t('settings.analytics.descPirsch')"></p>
        <div v-if="isDevEnv" class="dev-panel">
          <strong class="dev-panel-title">Nur lokal: echte Pirsch-Daten ansehen</strong>
          <div class="dev-panel-row">
            <input
              v-model="devPirschCode"
              type="text"
              placeholder="pirsch_code aus Produktion, z. B. KY13QlLar4"
            />
            <input
              v-model="devPattern"
              type="text"
              placeholder="Pfad-Muster, z. B. / oder /ausgabe-3"
            />
            <b-button size="sm" variant="primary" @click="reload">Anzeigen</b-button>
          </div>
          <p class="dev-panel-hint">
            Nur lesend. Der Lese-Endpunkt lässt einen fremden Code nur durch, wenn du Sysadmin bist.
          </p>
          <template v-if="rawPages.length">
            <strong class="dev-panel-title">Seiten, wie Pirsch sie liefert</strong>
            <p class="dev-panel-hint">
              Die Tabelle weiter unten bleibt lokal leer: sie ordnet die Zahlen den
              Seiten dieser Installation zu, und deren Pfade gibt es in den Live-Daten nicht.
            </p>
            <b-table
              :items="rawPages"
              :fields="devPageFields"
              dark
              borderless
              small
              class="mb-0"
            />
          </template>
        </div>
      </b-col>
    </b-row>
    <b-row class="row mb-4 mt-4">
      <b-col cols="12" sm="12">
        <p v-if="appliedRangeLabel" class="applied-date-range">
          {{ appliedRangeLabel }}
        </p>
        <b-row v-if="queryErrors.globalStats" class="global-stats mb-5">
          <b-col cols="12">
            <h4>Global stats</h4>
            <div class="query-error">
              {{ $t('settings.analytics.globalStatsQueryError') }}
            </div>
          </b-col>
        </b-row>
        <b-row v-else-if="globalStats" class="row global-stats mb-5">
          <b-col cols="12" sm="3">
            <h5>
              {{ $t('settings.analytics.visitors') }}
              <b-icon
                style="margin-left: 3px;"
                id="tooltip-global-visitors"
                icon="question-circle"
                aria-hidden="true">
              </b-icon>
              <b-tooltip target="tooltip-global-visitors">
                {{ $t('settings.analytics.pirschVisitorsTooltip') }}
              </b-tooltip>
            </h5>
            <div>
              {{ globalStats.visitors }}
              <div
                v-if="growthBadges.visitors"
                class="growth-badge"
                :class="'growth-' + growthBadges.visitors.tone"
                :title="$t('settings.plugins.analyticsViews.growthTooltip')"
              >
                <b-icon
                  :icon="growthBadges.visitors.direction === 'down' ? 'arrow-down' : 'arrow-up'"
                  scale="0.8"
                ></b-icon>
                {{ Math.abs(growthBadges.visitors.percent) }}&nbsp;%
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="3">
            <h5>
              {{ $t('settings.analytics.views') }}
              <b-icon
                style="margin-left: 3px;"
                id="tooltip-global-views"
                icon="question-circle"
                aria-hidden="true">
              </b-icon>
              <b-tooltip target="tooltip-global-views">
                {{ $t('settings.analytics.viewsTooltip') }}
              </b-tooltip>
            </h5>
            <div>
              {{ globalStats.views }}
              <div
                v-if="growthBadges.views"
                class="growth-badge"
                :class="'growth-' + growthBadges.views.tone"
                :title="$t('settings.plugins.analyticsViews.growthTooltip')"
              >
                <b-icon
                  :icon="growthBadges.views.direction === 'down' ? 'arrow-down' : 'arrow-up'"
                  scale="0.8"
                ></b-icon>
                {{ Math.abs(growthBadges.views.percent) }}&nbsp;%
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="3">
            <h5>
              {{ $t('settings.analytics.avgTime') }}
              <b-icon
                style="margin-left: 3px;"
                id="tooltip-global-avg-time"
                icon="question-circle"
                aria-hidden="true">
              </b-icon>
              <b-tooltip target="tooltip-global-avg-time">
                {{ $t('settings.analytics.pirschAvgPageTimeTooltip') }}
              </b-tooltip>
            </h5>
            <div>
              {{ globalStats.avgTime | timeString }}
              <div
                v-if="growthBadges.avgTime"
                class="growth-badge"
                :class="'growth-' + growthBadges.avgTime.tone"
                :title="$t('settings.plugins.analyticsViews.growthTooltip')"
              >
                <b-icon
                  :icon="growthBadges.avgTime.direction === 'down' ? 'arrow-down' : 'arrow-up'"
                  scale="0.8"
                ></b-icon>
                {{ Math.abs(growthBadges.avgTime.percent) }}&nbsp;%
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="3">
            <h5 id="global-bounce-rate">
              {{ $t('settings.analytics.bounceRate') }}
              <b-icon
                style="margin-left: 3px;"
                id="tooltip-global-bounce-rate"
                icon="question-circle"
                aria-hidden="true">
              </b-icon>
              <b-tooltip target="tooltip-global-bounce-rate">
                {{ $t('settings.analytics.bounceRateTooltip') }}
              </b-tooltip>
            </h5>
            <div>
              {{ globalStats.bounceRate }}
              <div
                v-if="growthBadges.bounceRate"
                class="growth-badge"
                :class="'growth-' + growthBadges.bounceRate.tone"
                :title="$t('settings.plugins.analyticsViews.growthTooltip')"
              >
                <b-icon
                  :icon="growthBadges.bounceRate.direction === 'down' ? 'arrow-down' : 'arrow-up'"
                  scale="0.8"
                ></b-icon>
                {{ Math.abs(growthBadges.bounceRate.percent) }}&nbsp;%
              </div>
            </div>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row class="mb-5">
      <b-col cols="12" sm="12">
        <h4>{{ $t('settings.analytics.readsPerDay') }}</h4>
        <div v-if="queryErrors.readsPerDayObject" class="query-error">
          {{ $t('settings.analytics.readsPerDayQueryError') }}
        </div>
        <LineChart
          v-else-if="readsPerDayObject"
          :key="readsPerDayKey"
          :reads-per-day-object="readsPerDayObject"
          :dateRange="dateRange"
        />
        <p v-else>{{ $t('settings.analytics.noReadsPerDay') }}</p>
      </b-col>
    </b-row>
    <b-row v-if="queryErrors.projectOrFolders" class="mb-5">
      <b-col cols="12" sm="12">
        <h4>{{ $t('settings.analytics.projectsOrFolders') }}</h4>
        <div class="query-error">
          {{ $t('settings.analytics.projectOrFoldersQueryError') }}
        </div>
      </b-col>
    </b-row>
    <b-row v-else-if="itemType === 'folder' && projectOrFolders.length" class="mb-5">
      <b-col cols="12" sm="12">
        <h4>{{ $t('settings.analytics.projectsOrFolders') }}</h4>

        <b-table
          :key="folderOrProjectTableKey"
          :fields="projectAndFolderFields"
          :items="projectOrFolders"
          borderless
          dark
          fixed
        >
          <template #table-colgroup="scope">
            <col
              v-for="field in scope.fields"
              :key="field.key"
              :style="{ width: field.width }"
            >
          </template>
          <template #cell(image)="data">
            <div
              v-if="data.item.type === 'folder'"
              class="cell-folder cell-background-image d-flex justify-content-center align-items-center"
            >
              <b-icon icon="folder" aria-hidden="true" scale="1"></b-icon>
            </div>
            <div
              v-else-if="data.item.type === 'project' && !data.item.image"
              class="cell-background-image d-flex justify-content-center align-items-center"
            >
              <b-icon icon="files" aria-hidden="true" scale="1"></b-icon>
            </div>
            <div
              v-else-if="data.item.type === 'project' && data.item.image"
              class="cell-background-image"
              :style="`background-image: ${data.item.image}`"
            ></div>
          </template>
          <template #cell(timeOnPage)="data">
            {{ data.item.timeOnPage | timeString }}
          </template>
        </b-table>
      </b-col>
    </b-row>
    <b-row v-if="queryErrors.pages" class="mb-5">
      <b-col cols="12" sm="6">
        <h4>{{ $t('settings.analytics.pages') }}</h4>
        <div class="query-error">
          {{ $t('settings.analytics.pagesQueryError') }}
        </div>
      </b-col>
    </b-row>
    <b-row v-else-if="pages && pages.length" class="mb-5">
      <b-col cols="12" sm="12">
        <h4>{{ $t('settings.analytics.pages') }}</h4>
        <b-table
          :key="pageTableKey"
          :fields="fields"
          :items="pages"
          borderless
          dark
          fixed
          class="pages-table"
          @row-clicked="togglePageDetails"
        >
          <template #table-colgroup="scope">
            <col
              v-for="field in scope.fields"
              :key="field.key"
              :style="{ width: field.width }"
            >
          </template>
          <template #cell(image)="data">
            <div
              class="cell-background-image"
              :style="{
                backgroundImage: data.item.imageObject.imageUrl,
                backgroundPosition: data.item.imageObject.focalpoint,
              }"
            ></div>
          </template>
          <template #cell(name)="data">
            <div class="name-cell">
              <div class="name-wrapper">
                {{ data.item.name }}
              </div>
            </div>
          </template>
          <template #cell(viewers)="data">
            <div class="viewers-cell">
              <div class="viewers-wrapper">
                {{ data.item.viewers }}
              </div>
            </div>
          </template>
          <template #cell(views)="data">
            <div class="views-cell">
              <div class="views-wrapper">
                {{ data.item.views }}
              </div>
            </div>
          </template>
          <template #cell(timeOnPage)="data">
            <div class="avg-reading-time-cell">
              <div class="avg-reading-time-wrapper">
                {{ data.item.timeOnPage | timeString }}
              </div>
            </div>
          </template>
          <template #head(readShare)="data">
            {{ data.label }}
            <b-icon
              id="tooltip-read-share"
              icon="question-circle"
              aria-hidden="true"
            ></b-icon>
            <b-tooltip target="tooltip-read-share">
              {{ $t('settings.plugins.analyticsViews.readShareTooltip') }}
            </b-tooltip>
          </template>
          <template #cell(readShare)="data">
            <div class="read-share-cell">
              {{ data.item.readShare === null ? '–' : data.item.readShare + ' %' }}
            </div>
          </template>
          <template #cell(bounceRate)="data">
            <div class="bounce-rate-cell">
              <div class="bounce-rate-wrapper">
                {{ data.item.bounceRate }}
              </div>
            </div>
          </template>
          <template #cell(details)="data">
            <b-icon
              :icon="data.item._showDetails ? 'chevron-down' : 'chevron-right'"
              scale="0.9"
            ></b-icon>
          </template>
          <template #row-details="data">
            <div class="page-details">
              <p v-if="!data.item.slugPath" class="mb-0">
                {{ $t('settings.plugins.analyticsViews.noLivePath') }}
              </p>
              <p v-else-if="!pageDetails[data.item.slugPath] || pageDetails[data.item.slugPath].loading" class="mb-0">
                {{ $t('settings.analytics.loading') }}
              </p>
              <p v-else-if="pageDetails[data.item.slugPath].error" class="query-error mb-0">
                {{ $t('settings.analytics.eventsQueryError') }}
              </p>
              <template v-else>
                <h6>
                  {{ $t('settings.plugins.analyticsViews.scrollDepth') }} ·
                  {{ $t('settings.plugins.analyticsViews.averageReadDepth') }}
                  {{ pageDetails[data.item.slugPath].average }}&nbsp;%
                </h6>
                <div
                  v-for="step in pageDetails[data.item.slugPath].steps"
                  :key="step.percent"
                  class="scroll-row"
                >
                  <span class="scroll-label">
                    {{ $t(`settings.plugins.analyticsViews.scrollStep${step.percent}`) }}
                  </span>
                  <span class="scroll-bar">
                    <span class="scroll-fill" :style="{ width: step.share + '%' }"></span>
                  </span>
                  <span class="scroll-value">{{ step.visitors }} · {{ step.share }}&nbsp;%</span>
                </div>

                <div
                  v-for="group in pageDetails[data.item.slugPath].links"
                  :key="group.key"
                  class="mt-3"
                >
                  <h6>{{ eventLabel(group.key) }}</h6>
                  <b-table
                    :items="group.rows"
                    :fields="detailLinkFields"
                    dark
                    borderless
                    fixed
                    class="mb-0"
                  >
                    <template #table-colgroup="scope">
                      <col
                        v-for="field in scope.fields"
                        :key="field.key"
                        :style="{ width: field.width }"
                      >
                    </template>
                    <template #cell(label)="cell">
                      <span class="link-cell" :title="cell.item.label">
                        {{ shortenUrl(cell.item.label, 90) }}
                      </span>
                    </template>
                  </b-table>
                </div>

                <div v-if="pageDetails[data.item.slugPath].forms.length" class="mt-3">
                  <h6>{{ $t('settings.plugins.analyticsViews.formFunnel') }}</h6>
                  <b-table
                    :items="pageDetails[data.item.slugPath].forms"
                    :fields="formFunnelFields"
                    dark
                    borderless
                    class="mb-0"
                  >
                    <template #cell(completionRate)="cell">{{ cell.item.completionRate }}%</template>
                  </b-table>
                </div>

                <p
                  v-if="!pageDetails[data.item.slugPath].links.length
                    && !pageDetails[data.item.slugPath].forms.length"
                  class="mt-3 mb-0"
                >
                  {{ $t('settings.plugins.analyticsViews.noLinks') }}
                </p>
              </template>
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <b-row v-if="queryErrors.entryExit" class="mb-5">
      <b-col cols="12" sm="12">
        <div class="query-error">{{ $t('settings.analytics.pagesQueryError') }}</div>
      </b-col>
    </b-row>
    <b-row v-else-if="entryPages.length || exitPages.length" class="mb-5">
      <b-col cols="12" xl="6">
        <h4>{{ $t('settings.plugins.analyticsViews.entryPages') }}</h4>
        <p class="scroll-desc mb-3">{{ $t('settings.plugins.analyticsViews.entryPagesDesc') }}</p>
        <b-table :items="entryPages" :fields="entryExitFields" class="entry-exit-table" dark borderless>
          <template #cell(name)="data">
            <div class="entry-exit-name">{{ data.item.name }}</div>
            <small class="link-cell text-muted" :title="data.item.path">{{ data.item.path }}</small>
          </template>
          <template #cell(rate)="data">{{ data.item.rate }}&nbsp;%</template>
        </b-table>
      </b-col>
      <b-col cols="12" xl="6">
        <h4>{{ $t('settings.plugins.analyticsViews.exitPages') }}</h4>
        <p class="scroll-desc mb-3">{{ $t('settings.plugins.analyticsViews.exitPagesDesc') }}</p>
        <b-table :items="exitPages" :fields="entryExitFields" class="entry-exit-table" dark borderless>
          <template #cell(name)="data">
            <div class="entry-exit-name">{{ data.item.name }}</div>
            <small class="link-cell text-muted" :title="data.item.path">{{ data.item.path }}</small>
          </template>
          <template #cell(rate)="data">{{ data.item.rate }}&nbsp;%</template>
        </b-table>
      </b-col>
    </b-row>
    <b-row class="mb-5">
      <b-col cols="12" sm="12">
        <h4 class="mb-4">{{ $t('settings.analytics.events') }}</h4>
        <div v-if="queryErrors.events" class="query-error">
          {{ $t('settings.analytics.eventsQueryError') }}
        </div>
        <template v-else-if="groupedEvents && Object.keys(groupedEvents).length">
          <b-card no-body class="mb-2" v-for="(events, category) in groupedEvents" :key="category">
            <b-card-header header-tag="header" role="tab">
              <b-button block v-b-toggle="category.replace(/\s+/g, '')" variant="dark" class="text-left event-category">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center">
                    <b-icon icon="chevron-down" aria-hidden="true" scale="1" class="chevron-icon"></b-icon>
                    <span class="ml-2">{{ eventLabel(category) }}</span>
                  </div>
                  <div class="event-summary">
                    <span class="mr-4">{{ getTotalVisitors(events) }} {{ $t('settings.analytics.visitors') }}</span>
                    <span>{{ getTotalCount(events) }} {{ $t('settings.analytics.events') }}</span>
                  </div>
                </div>
              </b-button>
            </b-card-header>
            <b-collapse :id="category.replace(/\s+/g, '')" accordion="events-accordion" role="tabpanel">
              <b-card-body class="p-0">
                <b-table
                  :items="events"
                  :fields="eventFields"
                  dark
                  borderless
                  fixed
                  class="mb-0"
                  :key="category.replace(/\s+/g, '')"
                >
                  <template #table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"
                      :key="field.key"
                      :style="{ width: field.width }"
                    >
                  </template>
                  <template #cell(label)="data">
                    <span class="link-cell" :title="(data.item.meta.label || '').trim()">
                      {{ shortenUrl(data.item.meta.label, 90) || '-' }}
                    </span>
                  </template>
                  <template #cell(action)="data">
                    {{ data.item.meta.action }}
                  </template>
                  <template #cell(visitors)="data">
                    {{ data.item.visitors }}
                  </template>
                  <template #cell(count)="data">
                    {{ data.item.count }}
                  </template>
                </b-table>
              </b-card-body>
            </b-collapse>
          </b-card>
        </template>
        <p v-else>{{ $t('settings.analytics.noEvents') }}</p>
      </b-col>
    </b-row>
    <b-row class="mb-5">
      <b-col cols="12" sm="12">
        <h4 class="mb-4">{{ $t('settings.plugins.analyticsViews.formFunnel') }}</h4>
        <b-card v-if="formFunnels.length" no-body class="mb-2">
          <b-card-header header-tag="header" role="tab">
            <b-button block v-b-toggle.form-funnel variant="dark" class="text-left event-category">
              <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <b-icon icon="chevron-down" aria-hidden="true" scale="1" class="chevron-icon"></b-icon>
                  <span class="ml-2">{{ formFunnels.length }} {{ $t('settings.plugins.analyticsViews.formFunnel') }}</span>
                </div>
                <div class="event-summary">
                  <span>{{ totalFormSubmissions }} {{ $t('settings.plugins.analyticsViews.submitted') }}</span>
                </div>
              </div>
            </b-button>
          </b-card-header>
          <b-collapse id="form-funnel" role="tabpanel">
            <b-card-body class="p-0">
              <b-table
                :items="formFunnels"
                :fields="formFunnelFields"
                dark
                borderless
                class="mb-0"
              >
                <template #cell(completionRate)="data">{{ data.item.completionRate }}%</template>
              </b-table>
            </b-card-body>
          </b-collapse>
        </b-card>
        <p v-else class="mb-0">{{ $t('settings.plugins.analyticsViews.noForms') }}</p>
      </b-col>
    </b-row>
    <b-row class="mb-5">
      <b-col cols="12" sm="12">
        <h4 class="mb-1">{{ $t('settings.plugins.analyticsViews.scrollDepth') }}</h4>
        <p class="scroll-desc mb-3">{{ $t('settings.plugins.analyticsViews.scrollDepthDesc') }}</p>
        <template v-if="scrollSteps.some((s) => s.visitors)">
          <div class="average-depth mb-3">
            {{ $t('settings.plugins.analyticsViews.averageReadDepth') }}:
            <strong>{{ averageDepth }}&nbsp;%</strong>
            <span class="average-base">
              {{ $t('settings.plugins.analyticsViews.averageReadDepthBase', { count: scrollSteps[0].visitors }) }}
            </span>
          </div>
          <div v-for="step in scrollSteps" :key="step.percent" class="scroll-row">
            <span class="scroll-label">{{ $t(`settings.plugins.analyticsViews.${step.labelKey}`) }}</span>
            <span class="scroll-bar"><span class="scroll-fill" :style="{ width: step.share + '%' }"></span></span>
            <span class="scroll-value">
              <span class="scroll-visitors">{{ step.visitors }}</span>
              <span class="scroll-share">{{ step.share }}&nbsp;%</span>
            </span>
          </div>
          <p v-if="panelScrolls" class="panel-scroll mt-3 mb-0">
            {{ $t('settings.plugins.analyticsViews.panelScroll') }}: {{ panelScrolls }}
            {{ $t('settings.analytics.visitors') }}
            <b-icon id="tooltip-panel-scroll" icon="question-circle" aria-hidden="true"></b-icon>
            <b-tooltip target="tooltip-panel-scroll">
              {{ $t('settings.plugins.analyticsViews.panelScrollDesc') }}
            </b-tooltip>
          </p>
        </template>
        <p v-else class="mb-0">{{ $t('settings.plugins.analyticsViews.noScroll') }}</p>
      </b-col>
    </b-row>
    <b-row class="mb-5">
      <b-col cols="12" sm="12">
        <h4>{{ $t('settings.analytics.referrers') }}</h4>
        <div v-if="queryErrors.referrers" class="query-error">
          {{ $t('settings.analytics.referrersQueryError') }}
        </div>
        <b-table
          v-else-if="referrers.length"
          :items="referrers"
          :fields="referrerFields"
          dark
          sort-by="visitors"
          sort-desc
          class="referrers-table"
        >
        </b-table>
        <p v-else>{{ $t('settings.analytics.noReferrers') }}</p>
      </b-col>
    </b-row>
    <b-row v-if="channels.length || campaigns.length" class="mb-5">
      <b-col cols="12" sm="6">
        <h4>{{ $t('settings.plugins.analyticsViews.channels') }}</h4>
        <b-table
          v-if="channels.length"
          :items="channels"
          :fields="channelFields"
          dark
          borderless
          sort-by="visitors"
          sort-desc
        />
      </b-col>
      <b-col cols="12" sm="6">
        <h4>{{ $t('settings.plugins.analyticsViews.campaigns') }}</h4>
        <b-table
          v-if="campaigns.length"
          :items="campaigns"
          :fields="campaignFields"
          dark
          borderless
          sort-by="visitors"
          sort-desc
        />
        <p v-else class="mb-0">{{ $t('settings.plugins.analyticsViews.noCampaigns') }}</p>
      </b-col>
    </b-row>
    <b-row class="mb-5">
      <b-col cols="12" sm="6">
        <h4>{{ $t('settings.analytics.deviceType') }}</h4>
        <div v-if="queryErrors.devices" class="query-error">
          {{ $t('settings.analytics.devicesQueryError') }}
        </div>
        <b-table
          v-else-if="devices.length"
          :items="devices"
          :fields="deviceFields"
          dark
          sort-by="visitors"
          sort-desc
          class="device-table"
        >
        </b-table>
        <p v-else>{{ $t('settings.analytics.noDevices') }}</p>
      </b-col>
      <b-col cols="12" sm="6">
        <h4>{{ $t('settings.analytics.countries') }}</h4>
        <div v-if="queryErrors.countries" class="query-error">
          {{ $t('settings.analytics.countriesQueryError') }}
        </div>
        <b-table
          v-else-if="countries.length"
          :items="countries"
          :fields="countryFields"
          dark
          sort-by="readers"
          sort-desc
          class="country-table"
        >
        </b-table>
        <p v-else>{{ $t('settings.analytics.noCountries') }}</p>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import SetLoadingIndicator from '@/graphQlQueries/mutations/setLoadingIndicatorInCache';
import AnalyticsDateRangePicker from '@/components/settings/analytics/AnalyticsDateRangePicker.vue';
import LineChart from '@/components/settings/analytics/LineChart.vue';
import {
  formatTimeString,
  getGlobalStats as computeGlobalStats,
  getReadsPerDay as computeReadsPerDay,
  fetchGlobalStats,
  fetchSessionDuration,
  fetchReadsPerDay,
  fetchScrollStepByPage,
  fetchPageEvents,
  fetchEntryPages,
  fetchExitPages,
  fetchGrowth,
  fetchChannels,
  fetchCampaigns,
  growthBadge,
  namePathRows,
  visitorsByPath,
  readSharePercent,
  eventLabel,
  shortenUrl,
  linkList,
  formFunnel,
  scrollDepth,
  averageReadDepth,
  panelScrollTotal,
  fetchStat,
  loadUrlLineages,
} from '@/components/settings/analytics/pirschStats';
import GetAllChildPageIdsOfFolder from '@/graphQlQueries/queries/getAllChildPageIdsOfFolder';
import GetAllChildPageIdsOfProject from '@/graphQlQueries/queries/getAllChildPageIdsOfProject';
import thumbDefault from '@/assets/thumb-default.png';
import GetAllFoldersAndProjectsByFolderId from '@/graphQlQueries/queries/getAllFoldersAndProjectsByFolderId';
import {
  normalizeSlugPath, slugPathOfItem, buildPathPattern,
} from '@/utils/analyticsPath';
import GetPageNamesBySlugPaths from '@/graphQlQueries/queries/getPageNamesBySlugPaths';

export default {
  name: 'PirschAnalyticsDashboard',
  components: { AnalyticsDateRangePicker, LineChart },
  props: {
    slugPathFromUrl: {
      type: String,
      default: null,
    },
    // Project id for URL-history lookup. Only project dashboards pass it;
    // folder dashboards have no URL history.
    historyProjectId: {
      type: Number,
      default: null,
    },
    groupDomain: {
      type: String,
      default: null,
    },
    itemId: {
      type: Number,
      default: null,
    },
    itemType: {
      type: String,
      default: null,
    },
    // Not required: locally there is no pirsch_code, and the dev panel
    // supplies one instead. In production the views only mount it with a code.
    pirschCode: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      urlLineage: null,
      queryErrors: {
        globalStats: false,
        readsPerDayObject: false,
        referrers: false,
        devices: false,
        countries: false,
        pages: false,
        projectOrFolders: false,
        events: false,
        entryExit: false,
      },
      dateRange: {
        startDate: null,
        endDate: null,
      },
      minDate: null,
      globalStats: null,
      growth: null,
      path: null,
      timezone: 'Europe/Berlin',
      switchDate: new Date('2024-04-01T00:00:00'),
      pirschDays: 0,
      readsPerDayObject: null,
      readsPerDayKey: 0,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
            },
          }],
        },
      },
      referrers: [],
      devices: [],
      countries: [],
      channels: [],
      campaigns: [],
      fieldCountries: [
        {
          key: 'name',
          label: '',
          sortable: false,
          width: 'auto',
        },
        {
          key: 'readers',
          label: this.$t('settings.analytics.readers'),
          sortable: true,
          width: '110px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
      ],
      pages: [],
      pageTableKey: 0,
      pageDetails: {},
      fields: [
        {
          key: 'image',
          label: '',
          sortable: false,
          width: '50px',
          thClass: 'image',
          tdClass: 'image',
        },
        {
          key: 'name',
          label: 'Name',
          sortable: false,
          width: 'auto',
        },
        {
          key: 'viewers',
          label: this.$t('settings.analytics.viewers'),
          sortable: true,
          width: '90px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'views',
          label: this.$t('settings.analytics.views'),
          sortable: true,
          width: '90px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'timeOnPage',
          label: this.$t('settings.analytics.timeOnPage'),
          sortable: true,
          width: '140px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'readShare',
          label: this.$t('settings.plugins.analyticsViews.readShare'),
          sortable: true,
          width: '120px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'bounceRate',
          label: this.$t('settings.analytics.bounceRate'),
          sortable: true,
          width: '120px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
      ],
      entryPages: [],
      exitPages: [],
      entryExitFields: [
        { key: 'name', label: this.$t('settings.analytics.pages'), sortable: false },
        {
          key: 'count', label: this.$t('settings.plugins.analyticsViews.sessions'), sortable: true, class: 'text-center',
        },
        {
          key: 'rate', label: this.$t('settings.plugins.analyticsViews.rate'), sortable: true, class: 'text-center',
        },
      ],
      projectOrFolders: [],
      folderOrProjectTableKey: 0,
      projectAndFolderFields: [
        {
          key: 'image',
          label: '',
          sortable: false,
          width: '50px',
          thClass: 'image',
          tdClass: 'image',
        },
        {
          key: 'name',
          label: 'Name',
          sortable: false,
          width: 'auto',
        },
        {
          key: 'viewers',
          label: this.$t('settings.analytics.viewers'),
          sortable: true,
          width: '80px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'views',
          label: this.$t('settings.analytics.views'),
          sortable: true,
          width: '90px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'bounceRate',
          label: this.$t('settings.analytics.bounceRate'),
          sortable: true,
          width: '120px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'details',
          label: '',
          sortable: false,
          width: '50px',
          tdClass: 'text-center',
        },
      ],
      devPageFields: [
        { key: 'path', label: 'Pfad', sortable: true },
        { key: 'visitors', label: 'Besucher', sortable: true },
        { key: 'views', label: 'Aufrufe', sortable: true },
      ],
      formFunnelFields: [
        { key: 'name', label: this.$t('settings.plugins.analyticsViews.formName'), sortable: true },
        { key: 'viewed', label: this.$t('settings.plugins.analyticsViews.viewed'), sortable: true },
        { key: 'started', label: this.$t('settings.plugins.analyticsViews.started'), sortable: true },
        { key: 'abandoned', label: this.$t('settings.plugins.analyticsViews.abandoned'), sortable: true },
        { key: 'submitted', label: this.$t('settings.plugins.analyticsViews.submitted'), sortable: true },
        { key: 'completionRate', label: this.$t('settings.plugins.analyticsViews.completionRate'), sortable: true },
      ],
      detailLinkFields: [
        {
          key: 'label',
          label: this.$t('settings.plugins.analyticsViews.target'),
          sortable: false,
          tdClass: 'detail-target-cell',
          width: 'auto',
        },
        {
          key: 'visitors',
          label: this.$t('settings.analytics.visitors'),
          sortable: true,
          class: 'text-center',
          width: '110px',
        },
        {
          key: 'count',
          label: this.$t('settings.plugins.analyticsViews.clicks'),
          sortable: true,
          class: 'text-center',
          width: '110px',
        },
      ],
      eventFields: [
        {
          key: 'label',
          label: 'Label',
          sortable: false,
          width: 'auto',
        },
        {
          key: 'action',
          label: 'Action',
          sortable: false,
          width: '150px',
        },
        {
          key: 'visitors',
          label: this.$t('settings.analytics.visitors'),
          sortable: true,
          width: '100px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        {
          key: 'count',
          label: 'Events',
          sortable: true,
          width: '100px',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
      ],
      groupedEvents: null,
      rawEvents: [],
      devPirschCode: '',
      devPattern: '',
      rawPages: [],
      referrerFields: [
        {
          key: 'referrer',
          label: this.$t('settings.analytics.referrers'),
          sortable: true,
        },
        {
          key: 'visitors',
          label: this.$t('settings.analytics.visitors'),
          sortable: true,
          class: 'text-center',
        },
      ],
      deviceFields: [
        {
          key: 'device',
          label: this.$t('settings.analytics.deviceType'),
          sortable: false,
        },
        {
          key: 'visitors',
          label: this.$t('settings.analytics.visitors'),
          sortable: true,
          class: 'text-center',
        },
      ],
      countryFields: [
        {
          key: 'name',
          label: this.$t('settings.analytics.countries'),
          sortable: false,
        },
        {
          key: 'readers',
          label: this.$t('settings.analytics.readers'),
          sortable: true,
          class: 'text-center',
        },
      ],
      channelFields: [
        { key: 'channel', label: this.$t('settings.plugins.analyticsViews.channel'), sortable: false },
        {
          key: 'visitors',
          label: this.$t('settings.analytics.visitors'),
          sortable: true,
          class: 'text-center',
        },
      ],
      campaignFields: [
        { key: 'campaign', label: this.$t('settings.plugins.analyticsViews.campaign'), sortable: false },
        {
          key: 'visitors',
          label: this.$t('settings.analytics.visitors'),
          sortable: true,
          class: 'text-center',
        },
      ],
      showSwitchDateModal: false,
    };
  },
  computed: {
    // Locally there is no pirsch_code: createGroup only registers a Pirsch
    // domain when APP_ENV is production. The dev panel lets you paste a real
    // code and path pattern to look at live numbers. Read-only, and it can
    // never render in a production build.
    isDevEnv() {
      return process.env.NODE_ENV !== 'production';
    },
    effectivePirschCode() {
      return (this.isDevEnv && this.devPirschCode) ? this.devPirschCode.trim() : this.pirschCode;
    },
    // Derived views over the raw event rows. The maths lives in pirschStats.js
    // so it stays unit-testable; this component only renders.
    formFunnels() {
      return formFunnel(this.rawEvents);
    },
    scrollSteps() {
      // The head figures are the honest base: a percentage of "everyone who
      // scrolled at all" answers a question nobody asked.
      const base = parseInt(this.globalStats?.visitors, 10) || 0;
      return scrollDepth(this.rawEvents, base)
        .map((step) => ({ ...step, labelKey: `scrollStep${step.percent}` }));
    },
    averageDepth() {
      return averageReadDepth(this.rawEvents);
    },
    panelScrolls() {
      return panelScrollTotal(this.rawEvents);
    },
    totalFormSubmissions() {
      return this.formFunnels.reduce((sum, form) => sum + form.submitted, 0);
    },
    // One badge per head figure. The bounce rate is the odd one out: falling
    // is the good direction there.
    growthBadges() {
      const raw = this.growth || {};
      return {
        visitors: growthBadge(raw.visitors_growth, false),
        views: growthBadge(raw.views_growth, false),
        avgTime: growthBadge(raw.time_spent_growth, false),
        bounceRate: growthBadge(raw.bounces_growth, true),
      };
    },
    showGroupDomainWarning() {
      return this.startDateBeforeSwitchDate;
    },
    startDateBeforeSwitchDate() {
      const startDate = new Date(this.dateRange.startDate);
      const switchDate = new Date(this.switchDate);
      return (startDate < switchDate);
    },
    switchDateFormat() {
      const date = this.switchDate;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    },
    appliedRangeLabel() {
      if (!this.dateRange.startDate || !this.dateRange.endDate) return '';
      const formatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
      const formatter = new Intl.DateTimeFormat(this.$i18n.locale, formatOptions);
      const start = formatter.format(new Date(this.dateRange.startDate));
      const end = formatter.format(new Date(this.dateRange.endDate));
      return this.$t('settings.analytics.dateRange.appliedLabel', {
        range: `${start} – ${end}`,
      });
    },
  },
  created() {
    this.initializeDates();
    this.path = this.getPath();
  },
  methods: {
    // Exposed to the template so the accordion headings read in German.
    eventLabel,
    shortenUrl,
    /**
     * Fetch every URL this project has held (project_url_history) as a
     * lineage: which paths to query over which dates. Failure degrades to
     * current-path-only; analytics must not break the settings page.
     */
    async loadUrlHistory() {
      if (!this.historyProjectId) return;
      try {
        const lineages = await loadUrlLineages(this.$apollo, [this.historyProjectId]);
        this.urlLineage = lineages.get(this.historyProjectId) || null;
      } catch (error) {
        this.urlLineage = null;
      }
    },
    /**
     * Options for every Pirsch statistic of this item. The lineage makes them
     * cover the project's former URLs too; the dev pattern override bypasses
     * it, since local paths never match the live data anyway.
     */
    statOpts() {
      const devOverride = this.isDevEnv && this.devPattern;
      return {
        pirschCode: this.effectivePirschCode,
        startDate: this.dateRange.startDate,
        endDate: this.dateRange.endDate,
        pattern: this.getItemPathPattern(),
        lineage: devOverride ? null : this.urlLineage,
      };
    },
    /**
     * The one entry point that loads everything.
     *
     * Pages, projects and events used to be started separately from mounted(),
     * so they ran once with whatever id existed at that moment and were never
     * repeated. Locally that meant they fired with a null id, failed, and
     * stayed failed after the dev panel supplied a code.
     */
    reload() {
      if (!this.effectivePirschCode) return;
      this.fetchStats();
      this.getThePageData();
      this.getTheProjectAndFolderData();
      this.getEvents();
      this.getChannelsAndCampaigns();
    },
    initializeDates() {
      const endDate = new Date();
      endDate.setHours(23, 59, 59, 0);
      endDate.setMonth(endDate.getMonth());
      this.dateRange.endDate = endDate.toISOString();

      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setHours(0, 0, 0, 0);
      this.dateRange.startDate = startDate.toISOString();

      this.minDate = new Date('2022-06-21T00:00:00').toISOString();
    },
    getPath() {
      return this.slugPathFromUrl || '/';
    },
    getPathWithSlash() {
      return this.path.endsWith('/') ? this.path : `${this.path}/`;
    },
    updateValues(dateRange) {
      const startDate = new Date(dateRange.startDate);
      const switchDate = new Date(this.switchDate);

      if (startDate < switchDate) {
        this.showSwitchDateModal = true;
      }

      this.dateRange = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      this.pageDetails = {};
      this.reload();
    },
    async fetchStats() {
      // Without a dashboard id every request is rejected by the read endpoint,
      // which locally means a wall of 400s before the dev panel is filled in.
      if (!this.effectivePirschCode) return;

      this.$apollo.mutate({
        mutation: SetLoadingIndicator,
        variables: { isIndicatorLoading: true },
      });

      try {
        // Fetch global stats
        try {
          const pirschGlobalStats = await this.getPirschGlobalStats();
          const pirschTimeOnPage = await this.getPirschTimeOnPage();
          this.globalStats = this.getGlobalStats(
            pirschGlobalStats, pirschTimeOnPage,
          );
          this.queryErrors.globalStats = false;
        } catch (error) {
          console.error('Error fetching global stats:', error);
          this.queryErrors.globalStats = true;
        }

        // Growth against the equally long period before. A failure here only
        // costs the arrows, so it never touches queryErrors.
        try {
          if (new Date(this.dateRange.endDate) >= this.switchDate) {
            this.growth = await fetchGrowth(this.$apollo, this.statOpts());
          } else {
            this.growth = null;
          }
        } catch (error) {
          console.error('Error fetching growth:', error);
          this.growth = null;
        }

        // Fetch reads per day data
        try {
          const pirschReadsPerDay = await this.getPirschReadsPerDay();
          this.readsPerDayObject = this.getReadsPerDay(pirschReadsPerDay);
          this.readsPerDayKey += 1;

          this.queryErrors.readsPerDayObject = false;
        } catch (error) {
          console.error('Error fetching reads per day:', error);
          this.queryErrors.readsPerDayObject = true;
        }

        // Fetch referrers data
        try {
          const pirschReferrers = await this.getPirschReferrers();
          this.referrers = this.getReferrers(pirschReferrers);
          this.queryErrors.referrers = false;
        } catch (error) {
          console.error('Error fetching referrers:', error);
          this.queryErrors.referrers = true;
        }

        // Fetch device data
        try {
          const pirschDevices = await this.getPirschDevices();
          this.devices = this.getDevices(pirschDevices);
          this.queryErrors.devices = false;
        } catch (error) {
          console.error('Error fetching devices:', error);
          this.queryErrors.devices = true;
        }

        // Fetch countries data
        try {
          const pirschCountries = await this.getPirschCountries();
          this.countries = this.getCountries(pirschCountries);
          this.queryErrors.countries = false;
        } catch (error) {
          console.error('Error fetching countries:', error);
          this.queryErrors.countries = true;
        }
      } catch (error) {
        console.error('General Error while fetching data:', error);
      } finally {
        this.$apollo.mutate({
          mutation: SetLoadingIndicator,
          variables: { isIndicatorLoading: false },
        });
      }
    },
    async getChannelsAndCampaigns() {
      if (new Date(this.dateRange.endDate) < this.switchDate) {
        // Stale rows must not survive a range change.
        this.channels = [];
        this.campaigns = [];
        return;
      }
      const opts = this.statOpts();
      try {
        const [channels, campaigns] = await Promise.all([
          fetchChannels(this.$apollo, opts),
          fetchCampaigns(this.$apollo, opts),
        ]);
        this.channels = (channels || [])
          .filter((row) => row.channel)
          .map((row) => ({ channel: row.channel, visitors: row.visitors || 0 }));
        this.campaigns = (campaigns || [])
          .filter((row) => row.utm_campaign)
          .map((row) => ({ campaign: row.utm_campaign, visitors: row.visitors || 0 }));
      } catch (error) {
        console.error('Error fetching channels and campaigns:', error);
        this.channels = [];
        this.campaigns = [];
      }
    },
    async getEvents() {
      if (new Date(this.dateRange.endDate) >= this.switchDate) {
        try {
          const events = await fetchStat(this.$apollo, '/statistics/event/list', this.statOpts());

          if (events) {
            const filteredEvents = events.filter(
              (event) => event.name !== 'Website-Unload' && event.name !== 'exit_intent',
            );
            this.rawEvents = filteredEvents;
            this.groupedEvents = this.groupEventsByCategory(filteredEvents);
          }
          this.queryErrors.events = false;
        } catch (error) {
          console.error('Error fetching events:', error);
          this.queryErrors.events = true;
        }
      }
    },
    // Scroll depth has its own block with a funnel and an average, so the raw
    // step rows would only repeat it in a form nobody can read.
    groupEventsByCategory(events) {
      return events.filter((event) => event.name !== 'Scroll depth').reduce((acc, event) => {
        const category = event.meta.category || 'Uncategorized';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(event);
        return acc;
      }, {});
    },
    getTotalVisitors(events) {
      return events.reduce((sum, event) => sum + event.visitors, 0);
    },
    getTotalCount(events) {
      return events.reduce((sum, event) => sum + event.count, 0);
    },
    async getPirschGlobalStats() {
      if (new Date(this.dateRange.endDate) >= this.switchDate) {
        return fetchGlobalStats(this.$apollo, this.statOpts());
      }
      return null;
    },
    async getPirschTimeOnPage() {
      if (new Date(this.dateRange.endDate) >= this.switchDate) {
        return fetchSessionDuration(this.$apollo, this.statOpts());
      }
      return null;
    },
    getGlobalStats(pirschData, pirschTimeOnPage) {
      return computeGlobalStats(pirschData, pirschTimeOnPage);
    },
    /**
     * The Pirsch path pattern for the item this dashboard is mounted on.
     *
     * Until 2026-08 this was the internal id-path (`/f<id>`, `/pr<id>`). The
     * runtime now reports the real public path (lib.js), so reading has to
     * follow — an id-path pattern finds none of the new hits. The account root
     * stays `/`, which matches everything below it, exactly as before.
     *
     * The pattern now also covers every URL the project held before (from
     * project_url_history), so a rename or move doesn't split its stats.
     */
    getItemPathPattern() {
      // Dev override: local paths never match the paths in the live data.
      if (this.isDevEnv && this.devPattern) return this.devPattern.trim();
      return buildPathPattern([this.slugPathFromUrl, ...((this.urlLineage && this.urlLineage.oldPaths) || [])]);
    },
    async getPirschReadsPerDay() {
      if (new Date(this.dateRange.endDate) >= this.switchDate) {
        return fetchReadsPerDay(this.$apollo, this.statOpts());
      }
      return null;
    },
    getReadsPerDay(pirschData) {
      return computeReadsPerDay(pirschData);
    },
    async getPirschReferrers() {
      if (new Date(this.dateRange.endDate) >= this.switchDate) {
        return fetchStat(this.$apollo, '/statistics/referrer', this.statOpts(), { limit: 10 });
      }
      return [];
    },
    getReferrers(pirschData) {
      const referrerMap = new Map();

      if (pirschData && pirschData.length > 0) {
        pirschData.forEach((item) => {
          const referrer = item.referrer_name || item.referrer || 'Direct / None';
          if (referrerMap.has(referrer)) {
            const existing = referrerMap.get(referrer);
            existing.visitors = existing._visitors + item.sessions;
            existing.views = existing._views + parseInt(item.visitors, 10);
            existing._visitors = existing.visitors;
            existing._views = existing.views;
          } else {
            referrerMap.set(referrer, {
              referrer,
              visitors: item.sessions,
              views: parseInt(item.visitors, 10),
              _visitors: item.sessions,
              _views: parseInt(item.visitors, 10),
            });
          }
        });
      }

      return Array.from(referrerMap.values())
        .sort((a, b) => b._visitors - a._visitors)
        .slice(0, 10);
    },
    async getPirschDevices() {
      if (new Date(this.dateRange.endDate) >= this.switchDate) {
        return fetchStat(this.$apollo, '/statistics/platform', this.statOpts());
      }
      return null;
    },
    getDevices(pirschData) {
      const deviceMap = new Map();

      if (pirschData && typeof pirschData === 'object') {
        const pirschTotal = pirschData.platform_desktop + pirschData.platform_mobile + pirschData.platform_unknown;
        if (pirschTotal > 0) {
          const deviceMapping = {
            desktop: 'desktop',
            mobile: 'mobile',
            unknown: 'unknown',
          };

          Object.entries(deviceMapping).forEach(([pirschDevice, mappedDevice]) => {
            const count = pirschData[`platform_${pirschDevice}`];
            const percentage = (count / pirschTotal) * 100;
            deviceMap.set(mappedDevice, {
              device: mappedDevice.charAt(0).toUpperCase() + mappedDevice.slice(1),
              visitors: `${Math.round(percentage)}%`,
              _visitors: percentage,
            });
          });
        }
      }

      ['desktop', 'mobile', 'unknown'].forEach((device) => {
        if (!deviceMap.has(device)) {
          deviceMap.set(device, {
            device: device.charAt(0).toUpperCase() + device.slice(1),
            visitors: '0%',
            _visitors: 0,
          });
        }
      });

      return Array.from(deviceMap.values());
    },
    async getPirschCountries() {
      if (new Date(this.dateRange.endDate) >= this.switchDate) {
        return fetchStat(this.$apollo, '/statistics/country', this.statOpts());
      }
      return null;
    },
    getCountries(pirschData) {
      const countryMap = new Map();
      let totalVisitors = 0;

      if (pirschData) {
        pirschData.forEach((item) => {
          const { visitors, country_code } = item;
          const countryCode = country_code === '\u0000\u0000' ? 'unknown' : country_code.toLowerCase();
          countryMap.set(countryCode, {
            name: countryCode === 'unknown' ? 'Unknown' : country_code.toUpperCase(),
            visitors,
          });
          totalVisitors += visitors;
        });
      }

      const countries = Array.from(countryMap.values()).map((country) => {
        const percentage = Math.round((country.visitors / totalVisitors) * 100);
        return {
          name: country.name,
          readers: percentage === 0 ? `0% (${country.visitors})` : `${percentage}%`,
          _readers: percentage,
        };
      });

      return countries;
    },
    async getThePageData() {
      try {
        let childPages;
        if (this.itemType === 'project') {
          const result = await this.$apollo.query({
            query: GetAllChildPageIdsOfProject,
            variables: { projectId: this.itemId },
            fetchPolicy: 'network-only',
          });
          childPages = result.data.projects[0]?.child_pages || [];
        } else {
          const result = await this.$apollo.query({
            query: GetAllChildPageIdsOfFolder,
            variables: { folderId: this.itemId },
            fetchPolicy: 'network-only',
          });
          childPages = result.data.folders[0]?.child_pages || [];
        }
        const [pirschData, readDepth] = await Promise.all([
          this.getPirschDataForChildPages(),
          this.getReadDepthByPage(),
        ]);

        // Local pages carry local item paths, which never match the paths in
        // live data. Keep the raw rows so the dev panel can show them as they
        // come from Pirsch.
        this.rawPages = pirschData || [];
        this.setChildPageData(childPages, pirschData, readDepth);
        await this.getEntryAndExitPages();
      } catch (error) {
        console.error('Error fetching page data:', error);
        this.queryErrors.pages = true;
      }
    },
    async getEntryAndExitPages() {
      if (new Date(this.dateRange.endDate) < this.switchDate) {
        // Stale rows must not survive a range change.
        this.entryPages = [];
        this.exitPages = [];
        return;
      }
      const opts = this.statOpts();
      const nameByPath = {};
      this.pages.forEach((page) => {
        if (page.slugPath) nameByPath[page.slugPath] = page.name;
      });
      try {
        const [entries, exits] = await Promise.all([
          fetchEntryPages(this.$apollo, opts),
          fetchExitPages(this.$apollo, opts),
        ]);
        Object.assign(nameByPath, await this.getNestedPageNames(nameByPath, [...(entries || []), ...(exits || [])]));
        this.entryPages = namePathRows(entries, nameByPath, 'entries', 10);
        this.exitPages = namePathRows(exits, nameByPath, 'exits', 10);
        this.queryErrors.entryExit = false;
      } catch (error) {
        console.error('Error fetching entry and exit pages:', error);
        this.queryErrors.entryExit = true;
      }
    },
    /**
     * Entry/exit rows on a folder are mostly pages inside its projects, which
     * this.pages (direct children only) doesn't know. Look their names up by
     * path. Failure keeps the path-derived fallback title.
     */
    async getNestedPageNames(nameByPath, rows) {
      const missing = [...new Set(rows
        .filter((row) => row && row.path)
        .map((row) => normalizeSlugPath(row.path))
        .filter((path) => !nameByPath[path]))];
      const names = {};
      if (!missing.length) return names;
      try {
        const result = await this.$apollo.query({
          query: GetPageNamesBySlugPaths,
          variables: { slugPaths: missing },
        });
        // ponytail: no group filter; the role's permissions scope item_lookup
        // to the user's groups. A sysadmin could see another group's name for
        // an identical path.
        (result.data.item_lookup || []).forEach((entry) => {
          if (entry.page && entry.page.name) names[entry.slug_path] = entry.page.name;
        });
      } catch (error) {
        console.error('Error fetching page names:', error);
      }
      return names;
    },
    async getPirschDataForChildPages() {
      if (new Date(this.dateRange.endDate) >= this.switchDate) {
        return fetchStat(this.$apollo, '/statistics/page', this.statOpts(), {
          include_avg_time_on_page: true,
        });
      }
      return [];
    },
    // Two steps, because the column is a share of the readers who scrolled at
    // all rather than of every visitor. Both numbers then come from the same
    // event stream and the column cannot be dragged down by a visitor count
    // that reaches further back than scroll tracking does.
    async getReadDepthByPage() {
      if (new Date(this.dateRange.endDate) < this.switchDate) {
        return { scrolled: {}, halfway: {} };
      }
      const opts = this.statOpts();
      try {
        const [scrolled, halfway] = await Promise.all([
          fetchScrollStepByPage(this.$apollo, opts, 25),
          fetchScrollStepByPage(this.$apollo, opts, 50),
        ]);
        return { scrolled: visitorsByPath(scrolled), halfway: visitorsByPath(halfway) };
      } catch (error) {
        // A missing reading depth leaves the column empty (not a made-up
        // zero); the table itself must still render.
        console.error('Error fetching read depth:', error);
        return null;
      }
    },
    setChildPageData(childPages, pirschData, readDepth) {
      this.pages = childPages.map((childPage) => {
        // Match on the public path the runtime reports, not the id-path.
        // A page without a lookup entry has no live path and stays at zero.
        const slugPath = slugPathOfItem(childPage);
        const pirschPageData = (pirschData && slugPath)
          ? pirschData.find((item) => normalizeSlugPath(item.path) === slugPath)
          : null;

        const viewers = pirschPageData ? pirschPageData.visitors : 0;
        const scrolled = (readDepth && slugPath) ? readDepth.scrolled[slugPath] : 0;
        const halfway = (readDepth && slugPath) ? readDepth.halfway[slugPath] : 0;

        const imageObject = {
          imageUrl: this.getPageImageUrl(childPage.thumbnails),
          focalpoint: childPage.thumbnails?.image?.focalpoint
            ? `${childPage.thumbnails.image.focalpoint.x}% ${childPage.thumbnails.image.focalpoint.y}%`
            : null,
        };
        return {
          name: childPage.name,
          slugPath,
          viewers,
          views: pirschPageData ? pirschPageData.views : 0,
          timeOnPage: pirschPageData ? pirschPageData.average_time_spent_seconds : 0,
          bounceRate: pirschPageData ? `${Math.round(pirschPageData.bounce_rate * 100)}%` : '0%',
          // A failed read-depth fetch (readDepth === null) must stay a dash,
          // not a confident 0 %, even for pages that do have visitors.
          readShare: readDepth ? readSharePercent(halfway, scrolled) : null,
          imageObject,
        };
      });
      this.pageTableKey += 1;
    },
    // Bound to the table's row-clicked event, so the whole row is the target
    // rather than a link on the name. `_showDetails` is BootstrapVue's own flag
    // and has to be set reactively, because rows arrive without it.
    async togglePageDetails(item) {
      this.$set(item, '_showDetails', !item._showDetails);
      const path = item.slugPath;
      if (!path || this.pageDetails[path]) return;

      this.$set(this.pageDetails, path, { loading: true, error: false });
      try {
        const events = (await fetchPageEvents(this.$apollo, {
          ...this.statOpts(),
          path,
        })) || [];
        this.$set(this.pageDetails, path, {
          loading: false,
          error: false,
          steps: scrollDepth(events, item.viewers),
          average: averageReadDepth(events),
          links: [
            { key: 'Navigate internal', rows: linkList(events, 'Navigate internal', 10) },
            { key: 'Navigate external', rows: linkList(events, 'Navigate external', 10) },
            { key: 'Download', rows: linkList(events, 'Download', 10) },
            { key: 'Contact', rows: linkList(events, 'Contact', 10) },
            { key: 'Custom', rows: linkList(events, 'Custom', 10) },
          ].filter((group) => group.rows.length),
          forms: formFunnel(events),
        });
      } catch (error) {
        console.error('Error fetching page events:', error);
        this.$set(this.pageDetails, path, { loading: false, error: true });
      }
    },
    getPageImageUrl(thumbnail) {
      let cloudImageUrl = null;
      if (thumbnail?.image?.url) {
        cloudImageUrl = this.$makeCloudImage(
          thumbnail.image.url,
          {
            width: 210,
            height: 150,
          },
          thumbnail.image,
        );
      }
      return (thumbnail?.image?.url)
        ? `url(${cloudImageUrl})`
        : `url(${thumbDefault})`; // Updated to use imported image
    },
    async getTheProjectAndFolderData() {
      if (this.itemType !== 'folder') return;

      this.$apollo.query({
        query: GetAllFoldersAndProjectsByFolderId,
        variables: {
          folderId: this.itemId,
        },
        fetchPolicy: 'network-only',
      }).then(async (result) => {
        const childItems = [
          ...result.data.folders.map((folder) => ({ ...folder, type: 'folder' })),
          ...result.data.projects.map((project) => ({ ...project, type: 'project' })),
        ];
        // Former URLs of the child projects, so a renamed project's tile keeps
        // its numbers. Without them the tiles fall back to the current path.
        const lineages = await loadUrlLineages(
          this.$apollo,
          result.data.projects.map((project) => project.project_id),
        ).catch(() => new Map());

        Promise.allSettled(childItems.flatMap((item) => [
          this.getPirschDataForChildItem(item, item.type === 'project' ? lineages.get(item.project_id) : null),
        ])).then((results) => {
          // Process results and update projectOrFolders
          this.projectOrFolders = childItems.map((item) => {
            // find the result item by the item_path
            const pirschData = results.find((r) => r.value?.childPath === item.item_path)?.value || {};

            return {
              name: item.name,
              type: item.type,
              image: this.getItemImageUrl(item),
              viewers: pirschData.viewers || 0,
              views: pirschData.views || 0,
              timeOnPage: pirschData.timeOnPage || 0,
              bounceRate: pirschData.bounce_rate ? `${Math.round(pirschData.bounce_rate * 100)}%` : '0%',
            };
          });

          // Trigger a re-render of the table
          this.folderOrProjectTableKey += 1;
        }).catch((error) => {
          console.error('Error fetching Analytics data:', error);
          this.queryErrors.projectOrFolders = true;
          this.projectOrFolders = childItems.map((item) => ({
            name: item.name,
            type: item.type,
            image: this.getItemImageUrl(item),
            viewers: 0,
            views: 0,
            timeOnPage: 0,
            bounceRate: 0,
          }));
        });
      });
    },
    getItemImageUrl(item) {
      if (item.type === 'folder') {
        return null; // Folders don't have images, we'll use an icon instead
      }
      let cloudImageUrl = null;
      if (item.thumbnails?.cover_image?.url) {
        cloudImageUrl = this.$makeCloudImage(
          item.thumbnails.cover_image.url,
          { width: 210, height: 150 },
          item.thumbnails.cover_image,
        );
      }
      return (item.thumbnails?.cover_image?.url)
        ? `url(${cloudImageUrl})`
        : `url(${thumbDefault})`;
    },
    getPirschDataForChildItem(childItem, lineage) {
      if (new Date(this.dateRange.endDate) < this.switchDate) {
        return Promise.resolve(null);
      }

      // Public path, not the id-path — same reason as in setChildPageData.
      // `childPath` below stays item_path: it is only the correlation key
      // used to line the result up with its row, never sent to Pirsch.
      const itemPath = slugPathOfItem(childItem);
      if (!itemPath || !this.effectivePirschCode) {
        return Promise.resolve(null);
      }

      return fetchStat(this.$apollo, '/statistics/total', {
        pirschCode: this.effectivePirschCode,
        startDate: this.dateRange.startDate,
        endDate: this.dateRange.endDate,
        pattern: itemPath,
        lineage: lineage || null,
      }, { include_avg_time_on_page: true }).then((result) => {
        if (!result) throw new Error('Invalid response structure from Pirsch API');
        return {
          viewers: parseInt(result.visitors, 10) || 0,
          views: parseInt(result.views, 10) || 0,
          timeOnPage: result.average_time_spent_seconds || 0,
          bounce_rate: parseFloat(result.bounce_rate) || 0,
          childPath: childItem.item_path,
        };
      }).catch((error) => {
        console.error('Error fetching Pirsch data for child item:', error);
        return {
          viewers: 0,
          views: 0,
          timeOnPage: 0,
          bounce_rate: 0,
          childPath: childItem.item_path,
        };
      });
    },
    getItemData(pirschData) {
      const entries = pirschData ? pirschData.visits : 0;
      const views = pirschData ? pirschData.pageviews : 0;
      const avgReadingTime = pirschData ? pirschData.avg_duration : 0;
      const bounceRate = pirschData ? pirschData.bounce_rate : 0;

      return {
        entries: entries || 0,
        views: views || 0,
        avgReadingTime: avgReadingTime || 0,
        bounceRate: bounceRate ? `${Math.round(bounceRate * 100)}%` : 0,
      };
    },
    calculateWeightedAverage(value1, value2, weight1, weight2) {
      const totalWeight = weight1 + weight2;
      if (totalWeight === 0) return 0;
      return ((value1 * weight1) + (value2 * weight2)) / totalWeight;
    },
    openEmail() {
      const subject = this.$t('settings.analytics.helpEmailSubject', { switchDate: this.switchDateFormat });
      const body = this.$t('settings.analytics.helpEmailBody', {
        switchDate: this.switchDateFormat,
        domain: this.groupDomain,
        path: this.getPathWithSlash(),
        startDate: this.dateRange.startDate.split('T')[0],
        endDate: this.dateRange.endDate.split('T')[0],
      });
      window.location.href = `mailto:support@webmag.io?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      this.showSwitchDateModal = false;
    },
    downloadCSV() {
      const csvRows = [];
      const delimiter = ';';

      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(delimiter) || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Helper function to format date
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE');
      };

      // Helper function to format time
      const formatTime = (seconds) => {
        if (!seconds) return '0';
        const totalSeconds = Math.floor(seconds);
        const secs = totalSeconds % 60;
        const mins = Math.floor(totalSeconds / 60);
        return (mins > 0) ? `${mins}min ${secs}s` : `${secs}s`;
      };

      // Add metadata
      csvRows.push('Analytics Export (Pirsch)');
      csvRows.push(`Date Range: ${formatDate(this.dateRange.startDate)} - ${formatDate(this.dateRange.endDate)}`);
      csvRows.push(`Domain: ${this.groupDomain || 'N/A'}`);
      csvRows.push(`Path pattern: ${this.getItemPathPattern()}`);
      csvRows.push('');

      // Global Stats
      if (this.globalStats) {
        csvRows.push('Global Statistics');
        csvRows.push(`Metric${delimiter}Value`);
        csvRows.push(`Visitors${delimiter}${escapeCSV(this.globalStats.visitors)}`);
        csvRows.push(`Views${delimiter}${escapeCSV(this.globalStats.views)}`);
        csvRows.push(`Average Time${delimiter}${escapeCSV(formatTime(this.globalStats.avgTime))}`);
        csvRows.push(`Bounce Rate${delimiter}${escapeCSV(this.globalStats.bounceRate)}`);
        csvRows.push('');
      }

      // Reads Per Day
      if (this.readsPerDayObject && this.readsPerDayObject.length > 0) {
        csvRows.push('Reads Per Day');
        csvRows.push(`Date${delimiter}Pageviews`);
        this.readsPerDayObject.forEach((item) => {
          csvRows.push(`${escapeCSV(item.label)}${delimiter}${escapeCSV(item.value)}`);
        });
        csvRows.push('');
      }

      // Projects or Folders
      if (this.projectOrFolders && this.projectOrFolders.length > 0) {
        csvRows.push('Projects / Folders');
        csvRows.push(`Name${delimiter}Type${delimiter}Viewers${delimiter}Views${delimiter}Time on Page${delimiter}Bounce Rate`);
        this.projectOrFolders.forEach((item) => {
          csvRows.push(
            `${escapeCSV(item.name)}${delimiter}${escapeCSV(item.type)}${delimiter}${escapeCSV(item.viewers)}${delimiter}${escapeCSV(item.views)}${delimiter}${escapeCSV(formatTime(item.timeOnPage))}${delimiter}${escapeCSV(item.bounceRate)}`,
          );
        });
        csvRows.push('');
      }

      // Pages
      if (this.pages && this.pages.length > 0) {
        csvRows.push('Pages');
        csvRows.push(`Name${delimiter}Viewers${delimiter}Views${delimiter}Time on Page${delimiter}Bounce Rate${delimiter}Read Halfway`);
        this.pages.forEach((item) => {
          const readShare = item.readShare === null ? '' : `${item.readShare}%`;
          csvRows.push(
            `${escapeCSV(item.name)}${delimiter}${escapeCSV(item.viewers)}${delimiter}${escapeCSV(item.views)}${delimiter}${escapeCSV(formatTime(item.timeOnPage))}${delimiter}${escapeCSV(item.bounceRate)}${delimiter}${escapeCSV(readShare)}`,
          );
        });
        csvRows.push('');
      }

      // Events
      if (this.groupedEvents && Object.keys(this.groupedEvents).length > 0) {
        csvRows.push('Events');
        csvRows.push(`Category${delimiter}Label${delimiter}Action${delimiter}Visitors${delimiter}Count`);
        Object.entries(this.groupedEvents).forEach(([category, events]) => {
          events.forEach((event) => {
            csvRows.push(
              `${escapeCSV(category)}${delimiter}${escapeCSV(event.meta?.label || '-')}${delimiter}${escapeCSV(event.meta?.action || '')}${delimiter}${escapeCSV(event.visitors)}${delimiter}${escapeCSV(event.count)}`,
            );
          });
        });
        csvRows.push('');
      }

      // Referrers
      if (this.referrers && this.referrers.length > 0) {
        csvRows.push('Referrers');
        csvRows.push(`Referrer${delimiter}Visitors${delimiter}Views`);
        this.referrers.forEach((item) => {
          csvRows.push(
            `${escapeCSV(item.referrer)}${delimiter}${escapeCSV(item.visitors)}${delimiter}${escapeCSV(item.views)}`,
          );
        });
        csvRows.push('');
      }

      // Device Types
      if (this.devices && this.devices.length > 0) {
        csvRows.push('Device Types');
        csvRows.push(`Device Type${delimiter}Visitors (%)`);
        this.devices.forEach((item) => {
          csvRows.push(
            `${escapeCSV(item.device)}${delimiter}${escapeCSV(item.visitors)}`,
          );
        });
        csvRows.push('');
      }

      // Countries
      if (this.countries && this.countries.length > 0) {
        csvRows.push('Countries');
        csvRows.push(`Country${delimiter}Readers (%)`);
        this.countries.forEach((item) => {
          csvRows.push(
            `${escapeCSV(item.name)}${delimiter}${escapeCSV(item.readers)}`,
          );
        });
        csvRows.push('');
      }

      // Reading depth
      if (this.scrollSteps.some((step) => step.visitors)) {
        csvRows.push('Reading Depth');
        csvRows.push(`Average${delimiter}${escapeCSV(`${this.averageDepth}%`)}`);
        csvRows.push(`Step${delimiter}Visitors${delimiter}Share`);
        this.scrollSteps.forEach((step) => {
          csvRows.push(`${step.percent}%${delimiter}${escapeCSV(step.visitors)}${delimiter}${escapeCSV(`${step.share}%`)}`);
        });
        csvRows.push('');
      }

      // Entry and exit pages
      if (this.entryPages.length) {
        csvRows.push('Entry Pages');
        csvRows.push(`Name${delimiter}Path${delimiter}Sessions${delimiter}Share`);
        this.entryPages.forEach((row) => {
          csvRows.push(`${escapeCSV(row.name)}${delimiter}${escapeCSV(row.path)}${delimiter}${escapeCSV(row.count)}${delimiter}${escapeCSV(`${row.rate}%`)}`);
        });
        csvRows.push('');
      }

      if (this.exitPages.length) {
        csvRows.push('Exit Pages');
        csvRows.push(`Name${delimiter}Path${delimiter}Sessions${delimiter}Share`);
        this.exitPages.forEach((row) => {
          csvRows.push(`${escapeCSV(row.name)}${delimiter}${escapeCSV(row.path)}${delimiter}${escapeCSV(row.count)}${delimiter}${escapeCSV(`${row.rate}%`)}`);
        });
        csvRows.push('');
      }

      // Channels and campaigns
      if (this.channels.length) {
        csvRows.push('Channels');
        csvRows.push(`Channel${delimiter}Visitors`);
        this.channels.forEach((row) => {
          csvRows.push(`${escapeCSV(row.channel)}${delimiter}${escapeCSV(row.visitors)}`);
        });
        csvRows.push('');
      }

      if (this.campaigns.length) {
        csvRows.push('Campaigns');
        csvRows.push(`Campaign${delimiter}Visitors`);
        this.campaigns.forEach((row) => {
          csvRows.push(`${escapeCSV(row.campaign)}${delimiter}${escapeCSV(row.visitors)}`);
        });
        csvRows.push('');
      }

      // Create and download the file
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      const fileName = `analytics-export-pirsch-${formatDate(this.dateRange.startDate)}-${formatDate(this.dateRange.endDate)}.csv`.replace(/\//g, '-');
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
  filters: {
    timeString: formatTimeString,
    inputDate(value) {
      if (!value) return value;
      const timestamp = new Date(value);
      if (!timestamp) return value;
      const month = timestamp.getMonth() + 1;
      const day = timestamp.getDate();
      return `${day}.${month}`;
    },
  },
  async mounted() {
    // The pattern the first Pirsch queries use must already include the
    // project's former URLs, so history has to land before reload() fires.
    await this.loadUrlHistory();
    this.reload();
  },
};
</script>

<style scoped lang="scss">
/* Tracked URLs can be hundreds of characters. Clip them to one line and put
   the full value in the title attribute. */
.link-cell {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Fixed layout keeps the number columns in the column instead of letting a
   long page name push them under the neighbouring table. */
.entry-exit-table ::v-deep table {
  table-layout: fixed;
}

.entry-exit-table ::v-deep th:not(:first-child) {
  width: 90px;
}

.entry-exit-name {
  overflow-wrap: anywhere;
}

.page-details {
  padding: 12px 8px 4px;
}

.dev-panel {
  border: 1px dashed #f0a500;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 12px;
}

.dev-panel-title {
  font-size: 12px;
  color: #f0a500;
}

.dev-panel-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.dev-panel-row input {
  flex: 1 1 240px;
  min-width: 0;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #4a4a4a;
  background: #2b2b2b;
  color: inherit;
}

.dev-panel-hint {
  margin: 8px 0 0;
  font-size: 11px;
  opacity: 0.7;
}

.scroll-desc {
  font-size: 12px;
  opacity: 0.7;
}

.scroll-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.scroll-label {
  width: 160px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.average-depth {
  font-size: 1.1rem;
}

.panel-scroll {
  opacity: 0.7;
  font-size: 0.9rem;
}

.scroll-bar {
  flex: 1;
  height: 14px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 7px;
  overflow: hidden;
}

.scroll-fill {
  display: block;
  height: 100%;
  background: #51d182;
}

.scroll-value {
  width: 70px;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

/* Count above, share below: side by side the two wrapped at different points
   from row to row, which made the column look ragged. */
.scroll-visitors,
.scroll-share {
  display: block;
}

.scroll-share {
  font-size: 0.8rem;
  opacity: 0.7;
}

.average-base {
  font-size: 0.85rem;
  opacity: 0.7;
  margin-left: 6px;
}

/* Tracked links are full URLs and push the table off the page without a cap. */
.detail-target-cell {
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pages-table tbody tr {
  cursor: pointer;
}

h3 {
  line-height: 38px;
}

.analytics-dashboard-header {
  gap: 0.75rem;
}

.applied-date-range {
  color: #b9b9b9;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin-bottom: 1rem;
  text-transform: uppercase;
}

.domain-select {
  max-width: 350px;

  select {
    width: 100%;
  }
}

h4, h5 {
  text-transform: uppercase;
  font-weight: 600;
}

h4 {
  color: #fff;
}

.global-stats {
  h5 {
    font-size: 13px;
    color: #b9b9b9;
  }

  div {
    font-size: 33px;
    font-weight: 400;
    color: #fff
  }

  // The badge is a div inside this block, so the rule above would paint it
  // white at 33px. These have to live here to outweigh it.
  .growth-badge {
    font-size: 13px;
    margin-top: 2px;
  }

  .growth-good {
    color: #4caf50;
  }

  .growth-bad {
    color: #e57373;
  }

  .growth-neutral {
    color: #b9b9b9;
  }
}

.table-dark {
  background-color: transparent;
}

::v-deep  table {
  font-size: 15px;
  border-collapse: separate;
  border-spacing: 0 8px;

  thead th {
    text-transform: uppercase;
    font-size: 13px;
    border-color: transparent;
  }

  tbody tr {
    background-color: #53575B;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);

    > td {
      padding: 7px 8px;

      &.image:first-child {
        padding: 0;
        max-width: 50px;
      }

      &.center {
        text-align: center;
        font-weight: 700;
      }

      &:nth-child(4) {
        ul {
          list-style: none;
          padding-left: 0;
          margin-bottom: 0;

          li {
            display: block;
            padding: 0 5px;
            margin-right: 6px;
            font-size: 13px;
            font-weight: 700;
            color: #44484C;
            background-color: #B9B9B9;
            text-transform: uppercase;
          }
        }
      }
    }
  }

  .cell-background-image {
    width: 100%;
    height: 100%;
    min-height: 36px;
    min-width: 50px;
    background-color: #777C7F;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
  }

  .cell-folder {
    background: rgba(83, 220, 134, 0.7);

    svg {
      fill: white;
    }
  }
}

hr {
  margin-top: 0;
}

::v-deep  #line-chart {
  min-height: 400px;
}

#content .query-error {
  font-family: 'Source Sans Pro', sans-serif;
  color: #fff;
  background-color: #53575B;
  border: 1px solid rgba(220, 83, 83, 0.7);
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
  font-size: 15px;
  padding: 7px 8px;
}

::v-deep  .btn.dot-button {
  color: #b9b9b9;
  background: transparent;
  border: none;
  margin-bottom: 0.5rem;

  &:hover {
    color: #fff;
  }
}

.context-menu {
  margin-left: 0.5rem;
}

.country-table ::v-deep  tr > td {
  text-transform: uppercase;
}

.stats-breakdown {
  color: #b9b9b9;

  h5 {
    color: #fff;
    margin-bottom: 15px;
  }

  p {
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .stat-value {
    font-weight: bold;
    color: #fff;
  }
}

.table-simple {
  width: 100%;

  th, td {
    padding: 10px;
    text-align: left;
  }

  th {
    font-weight: bold;
    text-transform: uppercase;
  }

}

.device-table, .country-table, .referrers-table {
  th:nth-child(2), th:nth-child(3) {
    width: 110px;
    text-align: center;
  }
  td:nth-child(2), td:nth-child(3) {
    text-align: center;
  }
}

.cell-background-image {
  width: 100%;
  height: 100%;
  min-height: 36px;
  min-width: 50px;
  background-color: #777C7F;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
}

.cell-folder {
  background: rgba(83, 220, 134, 0.7);

  svg {
    fill: white;
  }
}

.category {
  background-color: #53575B;
  border: none !important;

  &:hover, &:focus {
    background-color: #53575B !important;
  }
}
.event-category {
  background-color: #53575B;
  border: none !important;
}

.summary {
  font-size: 0.9em;
  color: #B9B9B9;
}

::v-deep  .card {
  background-color: transparent;
  border: none;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
  .card-header {
    background-color: transparent;
    border: none;
    padding: 0;
    .chevron-icon {
      transition: transform 0.2s ease;
    }

    .not-collapsed .chevron-icon {
      transform: rotate(180deg);
    }
  }

  .card-body {
    background-color: #44484c;
  }

  table {
    margin-bottom: 0 !important;

    tbody tr {
      background-color: transparent;
      box-shadow: none;
    }
  }
}

</style>
